import React, { useEffect, useMemo, useState } from 'react';
import {
	getCompanyMetadataOptions,
	getRandomAlias,
	updateCompaniesMetadata,
} from 'utils/apiRequests';
import { SettingsContext } from 'contexts/SettingsContext';
import { CompanyTile } from './CompanyTile';
import { SaveMetaDataActionWrapper, Wrapper } from './CompanyMetadata.styles';
import { SpendCubeSettingsContext } from 'contexts/SpendCubeSettingsContext';
import { isNull } from 'lodash';
import { ApButtonMain, ApLoaderDefault } from '@alixpartners/ui-components';

const INITIAL_OPTIONS = {
	employeesNumberBands: [],
	geographies: [],
	headOfficeLocations: [],
	industries: [],
	revenues: [],
};

const NOT_SET = 'Not set';

const CompanyMetaData = ({
	companiesMetadata,
	refetchCompaniesMetadata,
	refetchSpendContext,
}) => {
	const [options, setOptions] = useState(INITIAL_OPTIONS);
	const [localCompaniesMetadata, setLocalCompaniesMetadata] = useState([]);
	const [isAnyAliasAssigned, setIsAnyAliasAssigned] = useState(false);
	const [isMetaDataSaving, setIsMetaDataSaving] = useState(false);
	useEffect(() => {
		refetchCompaniesMetadata();
		getCompanyMetadataOptions().then(result => {
			const mappedOptions = Object.keys(result).reduce((acc, key) => {
				return {
					...acc,
					[key]: result[key].data,
				};
			}, {});
			setOptions(mappedOptions);
		});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(
		() => {
			if (localCompaniesMetadata.length === 0) {
				if (companiesMetadata.companies.length > 0) {
					const isPresent = checkIfAnyAliasPresent(companiesMetadata.companies);
					if (!isAnyAliasAssigned) {
						setIsAnyAliasAssigned(isPresent);
					}
				}
				setLocalCompaniesMetadata(companiesMetadata.companies);
			}
		}, // eslint-disable-next-line
		[companiesMetadata]
	);

	useEffect(
		() => {
			if (!isAnyAliasAssigned) {
				if (localCompaniesMetadata.length > 0) {
					// assign alias
					const flag = checkIfAnyAliasPresent(localCompaniesMetadata);
					if (!flag) {
						const count = localCompaniesMetadata.length;
						getRandomAlias(1).then(response => {
							if (response.succeeded) {
								const randomAlias = response.data[0];
								if (count > 1) {
									const data = localCompaniesMetadata.map((item, index) => {
										return {
											...item,
											aliasName: `${randomAlias}_${index + 1}`,
										};
									});
									setLocalCompaniesMetadata(data);
								} else {
									const aliasResult = localCompaniesMetadata[0];
									aliasResult.aliasName = randomAlias;
									setLocalCompaniesMetadata([aliasResult]);
								}
							}
						});
					}
				}
			} else {
				const isAliasPresentForAll = checkIfAliasPresentForAll(
					localCompaniesMetadata
				);
				if (!isAliasPresentForAll) {
					const alreadyUsedAlias = findAliasName(localCompaniesMetadata);
					const companiesWithAlias = localCompaniesMetadata.filter(
						el => el.aliasName !== null
					);
					const count = companiesWithAlias.length;
					const updatedCompaniesData = addAliasForRemainingCompanies(
						localCompaniesMetadata,
						count,
						alreadyUsedAlias
					);
					setLocalCompaniesMetadata(updatedCompaniesData);
				}
			}
		}, // eslint-disable-next-line
		[isAnyAliasAssigned, localCompaniesMetadata]
	);

	const checkIfAnyAliasPresent = data => {
		let isPresent = false;
		data.forEach(item => {
			if (item.hasOwnProperty('aliasName')) {
				if (item.aliasName === '' || item.aliasName === null) {
					// alias not present, assign alias
				} else {
					// alias present, do not assign alias
					isPresent = true;
				}
			}
		});
		return isPresent;
	};

	const checkIfAliasPresentForAll = data => {
		for (const obj of data) {
			if (
				!obj.hasOwnProperty('aliasName') ||
				obj.aliasName === null ||
				obj.aliasName.trim() === ''
			) {
				return false;
			}
		}
		return true;
	};

	const findAliasName = arr => {
		let aliasName = '';
		for (const obj of arr) {
			if (obj.hasOwnProperty('aliasName')) {
				if (!isNull(obj.aliasName)) {
					aliasName = obj.aliasName.split('_')[0];
				}
			}
		}
		return aliasName;
	};

	const addAliasForRemainingCompanies = (data, count, prefix) => {
		let nullAliasCount = 0;
		const processedData = data.map(obj => {
			if (obj.aliasName === null || obj.aliasName.trim() === '') {
				nullAliasCount++;
				return {
					...obj,
					aliasName: `${prefix}_${count + nullAliasCount}`,
				};
			} else {
				return obj;
			}
		});
		return processedData;
	};

	const onUpdateCompany = (companyId, field, value) => {
		const newCompanies = [...localCompaniesMetadata];
		let fieldValue = null;

		if (field === 'employeesNumber') {
			fieldValue = value || null;
		} else if (field === 'revenue' && value) {
			fieldValue = {
				data: { value },
			};
		} else if (field === 'year') {
			fieldValue = value === NOT_SET ? null : value;
		} else {
			fieldValue =
				value === NOT_SET
					? null
					: {
							data: {
								key: value,
							},
					  };
		}
		newCompanies[companyId][field] = fieldValue;
		setLocalCompaniesMetadata(newCompanies);
		// updateCompaniesMetadata(newCompanies).then(refetchCompaniesMetadata);
	};

	const onSaveMetaDataHandler = () => {
		setIsMetaDataSaving(true);
		updateCompaniesMetadata(localCompaniesMetadata).then(() => {
			refetchCompaniesMetadata().then(() => {
				setIsMetaDataSaving(false);
				refetchSpendContext();
			});
		});
	};

	const isSaveEnable = useMemo(
		() => {
			return localCompaniesMetadata.every(
				obj =>
					obj.year !== null &&
					obj.revenue !== null &&
					obj.industry !== null &&
					obj.industry?.data?.key !== null &&
					obj.revenue?.data?.value !== undefined &&
					obj.revenue?.data?.value !== null &&
					!isNaN(obj.revenue?.data?.value)
			);
		},
		[localCompaniesMetadata]
	);

	return (
		<Wrapper>
			{localCompaniesMetadata.map((metadata, id) => (
				<>
					<CompanyTile
						key={`${metadata.companyName}`}
						{...metadata}
						options={options}
						companyId={id}
						companyYear={metadata.year}
						companyAliasName={metadata.aliasName}
						revenue={metadata.revenue ? metadata.revenue.data.value : null}
						onUpdateCompany={onUpdateCompany}
					/>
				</>
			))}
			<SaveMetaDataActionWrapper>
				{!isMetaDataSaving && (
					<ApButtonMain
						onClick={onSaveMetaDataHandler}
						disabled={!isSaveEnable}
					>
						Save
					</ApButtonMain>
				)}
				{isMetaDataSaving && <ApLoaderDefault loaderSize="sm" />}
			</SaveMetaDataActionWrapper>
		</Wrapper>
	);
};

export default props => (
	<SpendCubeSettingsContext.Consumer>
		{({ refetch: refetchSpendContext }) => (
			<SettingsContext.Consumer>
				{({ companiesMetadata, refetchCompaniesMetadata }) => (
					<CompanyMetaData
						{...props}
						companiesMetadata={companiesMetadata}
						refetchSpendContext={refetchSpendContext}
						refetchCompaniesMetadata={refetchCompaniesMetadata}
					/>
				)}
			</SettingsContext.Consumer>
		)}
	</SpendCubeSettingsContext.Consumer>
);
